import { Box, Spinner, Button, ButtonGroup, Spacer } from '@chakra-ui/react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { groupBy, sum } from 'lodash';
import { AutodeskViewing } from '../components/viewers/common/AutodeskViewing';
import {
  ForgeViewerProvider,
  useViewer,
  DbIdsPerModel,
} from '../components/common/ForgeViewer';
import {
  GetProjectSettingsDocument,
  GetShapesForProjectDeepDocument,
  GetOrderDeepDocument,
  GetOrderDeepQuery,
  OrderEntryDeepFragment,
  ShapeDeepFragment,
  GetSheetShapesForProjectDeepDocument,
  SheetShapeDeepFragment,
} from '../gql/graphql';
import { extractFirstHexColor } from '../utils/color-util';
import {
  ViewerMode,
  ViewerModeProvider,
  useViewerMode,
} from '../components/common/viewer-mode';
import GhostModeButton from '../components/common/GhostModeButton';
import IsolatedElementsButton from '../components/common/forgeViewerInteractions/IsolatedElementsButton';
import HiddenElementsButton from '../components/common/forgeViewerInteractions/HiddenElementsButton';
import MeasureToolButton from '../components/common/forgeViewerInteractions/MeasureToolButton';
import { useLocalStorage } from '../hooks/local-storage';
import { ForgeContextMenuProvider } from '../services/viewer/ForgeContextMenu';
import { SheetContextMenuProvider } from '../services/viewer/SheetsContextMenu';
import { getShapeUrn } from '../services/viewer/ShapesManager';
import { useVisibility } from '../services/viewer/visibility';
import { useColorization } from '../services/viewer/colorization';
import { SelectedEntryContext } from './SelectedEntryContext';
import { CachedEntry } from './MobileOrderEntries';
import { ProjectHistoryProvider } from 'src/components/project/project-history/hooks/useProjectHistory';
import SheetViewerProvider from 'src/components/common/SheetViewer';
import { SheetsViewerV2 } from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import { LegendEntriesProvider } from 'src/components/viewers/SheetsViewerV2/hooks/LegendEntriesContext';
import { useEnabledSheetViewerModeValue } from 'src/components/viewers/common/hooks/useEnabledSheetViewerModeValue';
import { MeshAlgorithmResultsProvider } from 'src/hooks/mesh-algorithm-results';
import { useUserTenant } from 'src/services/auth-info';

interface Params {
  projectId: string;
  orderId?: string;
}

// New component for rendering project shapes
interface ProjectShapesRendererProps {
  projectId: string;
  shapes: ShapeDeepFragment[];
}

function ProjectShapesRenderer({
  projectId,
  shapes,
}: ProjectShapesRendererProps): null {
  const { shapesManager } = useViewer();
  const shapeUrn = getShapeUrn(projectId);
  const activeRenderedUrnRef = useRef<string | null>(null);

  // Effect for rendering shapes
  useEffect(() => {
    if (!shapesManager || !shapeUrn || shapes.length === 0) {
      if (!shapeUrn && activeRenderedUrnRef.current) {
        activeRenderedUrnRef.current = null;
      }
      return;
    }

    if (activeRenderedUrnRef.current !== shapeUrn) {
      shapesManager.renderShapes(shapeUrn, shapes);
      activeRenderedUrnRef.current = shapeUrn;
    } else {
      shapesManager.renderShapes(shapeUrn, shapes);
    }
  }, [shapesManager, shapeUrn, shapes]);

  // Effect for cleaning up shapes when shapeUrn changes or component unmounts
  useEffect(() => {
    const managerAtSetup = shapesManager;
    const urnTrackedByThisEffect = shapeUrn;

    if (!urnTrackedByThisEffect || !managerAtSetup) {
      return;
    }

    return () => {
      managerAtSetup.unrenderShapes(urnTrackedByThisEffect);
      if (activeRenderedUrnRef.current === urnTrackedByThisEffect) {
        activeRenderedUrnRef.current = null;
      }
    };
  }, [shapeUrn]); // This effect intentionally ONLY depends on `shapeUrn`.

  return null; // This component does not render layout
}

function MobileViewerSettings(): React.ReactElement {
  const {
    viewer,
    visibilityManager: {
      hiddenElements,
      isolatedElements,
      setHiddenElements,
      setIsolatedElements,
    },
  } = useViewer();
  const { viewerMode, setViewerMode } = useViewerMode();
  const { sheetsViewerMode } = useEnabledSheetViewerModeValue();

  const [isMeasureActive, setMeasureActive] = useState(false);

  const measureExtension = useMemo(() => {
    const extension = viewer?.getExtension(
      'Autodesk.Measure'
    ) as Autodesk.Extensions.Measure.MeasureExtension | null;
    extension?.setUnits('m');
    extension?.setPrecision(3);
    return extension;
  }, [viewer]);

  const numHiddenElements = sum(
    Object.values(hiddenElements).map((v) => v.length)
  );
  const numIsolatedElements = sum(
    Object.values(isolatedElements).map((v) => v.length)
  );

  const isModelViewer = viewerMode === ViewerMode.Models;

  return (
    <Box position="absolute" top={4} left={4} right={4} zIndex={10}>
      <ButtonGroup gap={2} width="100%" alignItems="center">
        <ButtonGroup size="xs" isAttached variant="outline">
          <Button
            onClick={() => setViewerMode(ViewerMode.Models)}
            variant={isModelViewer ? 'solid' : 'outline'}
          >
            Models
          </Button>
          <Button
            onClick={() => setViewerMode(sheetsViewerMode)}
            variant={viewerMode === sheetsViewerMode ? 'solid' : 'outline'}
          >
            Sheets
          </Button>
        </ButtonGroup>
        {isModelViewer ? (
          <>
            <GhostModeButton />
            <MeasureToolButton
              isMeasureActive={isMeasureActive}
              onToggleMeasure={() => {
                isMeasureActive
                  ? measureExtension?.deactivate()
                  : measureExtension?.activate('distance');
                setMeasureActive((active) => !active);
              }}
            />
            <Spacer />
            <IsolatedElementsButton
              numIsolatedElements={numIsolatedElements}
              onClearIsolated={() => setIsolatedElements({})}
            />
            <HiddenElementsButton
              numHiddenElements={numHiddenElements}
              onClearHidden={() => setHiddenElements({})}
            />
          </>
        ) : (
          <Spacer />
        )}
      </ButtonGroup>
    </Box>
  );
}

function MobileSheetBootstrap(): null {
  const { viewerMode } = useViewerMode();
  const { sheetsViewerMode } = useEnabledSheetViewerModeValue();
  const { sheets, activeSheetId, changeActiveSheet } = useSheetViewerV2();

  useEffect(() => {
    if (viewerMode !== sheetsViewerMode) {
      return;
    }

    if (activeSheetId || !sheets || sheets.length === 0) {
      return;
    }

    changeActiveSheet(sheets[0].id);
  }, [activeSheetId, changeActiveSheet, sheets, sheetsViewerMode, viewerMode]);

  return null;
}

// Component to load sheet shapes into the shapes manager
function MobileSheetShapesLoader({ projectId }: { projectId: string }): null {
  const { shapesManager, activeSheetId, pagination } = useSheetViewerV2();
  const { viewerMode } = useViewerMode();
  const { sheetsViewerMode } = useEnabledSheetViewerModeValue();

  // Fetch sheet shapes for the project
  const [{ data: sheetShapesData }] = useQuery({
    query: GetSheetShapesForProjectDeepDocument,
    variables: { projectId },
  });

  const sheetShapes = useMemo(() => {
    return sheetShapesData?.project?.sheetShapes ?? [];
  }, [sheetShapesData]);

  // Load sheet shapes when active sheet/page changes
  useEffect(() => {
    // Only load shapes when in sheets viewer mode
    if (viewerMode !== sheetsViewerMode) {
      return;
    }

    if (!activeSheetId || !sheetShapes.length) {
      return;
    }

    const shapesForSheetAndPage = sheetShapes
      .filter(
        (shape: SheetShapeDeepFragment) =>
          shape.sheetId === activeSheetId &&
          shape.sheetPageNumber === pagination.pageNumber
      )
      .sort((a: SheetShapeDeepFragment, b: SheetShapeDeepFragment) => {
        const getPriority = (shape: SheetShapeDeepFragment) => {
          if (shape.sheetShapePoint) return 2; // Highest priority (render last)
          if (shape.sheetShapeLine) return 1; // Next highest priority
          return 0; // Default priority (render first)
        };
        return getPriority(a) - getPriority(b);
      });

    const shapesByUrn = groupBy(shapesForSheetAndPage, (shape) => shape.urn);
    for (const urn of Object.keys(shapesByUrn)) {
      shapesManager.renderSheetShapes(urn, shapesByUrn[urn]);
    }

    return () => {
      for (const urn of Object.keys(shapesByUrn)) {
        shapesManager.unrenderSheetShapes(urn);
      }
    };
  }, [
    activeSheetId,
    pagination.pageNumber,
    sheetShapes,
    shapesManager,
    viewerMode,
    sheetsViewerMode,
  ]);

  return null;
}

// eslint-disable-next-line complexity
export default function ForgeViewerMobile(): React.ReactElement {
  const { projectId, orderId } = useParams<keyof Params>() as Params;
  const { isProtan } = useUserTenant();

  const [isViewingInitialized, setIsViewingInitialized] =
    useState<boolean>(false);
  const [viewerMode, setViewerMode] = useLocalStorage<ViewerMode>(
    `viewer-mode-${projectId}`,
    isProtan ? ViewerMode.Sheets : ViewerMode.Models
  );

  const viewerModeContextValue = useMemo(
    () => ({
      viewerMode,
      setViewerMode,
    }),
    [setViewerMode, viewerMode]
  );

  const [{ data: projectData, fetching: projectFetching }] = useQuery({
    query: GetProjectSettingsDocument,
    variables: { projectId },
  });

  // Re-add query for project shapes
  const [{ data: shapesData, fetching: shapesFetching }] = useQuery({
    query: GetShapesForProjectDeepDocument,
    variables: { projectId },
  });

  // Query for order entries - only run when orderId is present
  const [{ data: orderData, fetching: orderFetching }] =
    useQuery<GetOrderDeepQuery>({
      query: GetOrderDeepDocument,
      variables: { id: orderId! },
      pause: !orderId, // Pause the query when orderId is undefined
    });

  const shapes = useMemo(() => {
    return shapesData?.project?.shapes ?? [];
  }, [shapesData]);

  const { selectedIds } = useContext(SelectedEntryContext);

  // Convert selected IDs to actual entry objects - only when we have order data
  const selectedEntries = useMemo(() => {
    if (!orderData?.order?.orderEntries || selectedIds.size === 0 || !orderId) {
      return [];
    }

    const allEntries: CachedEntry[] = [];

    // Helper function to recursively collect all entries
    const collectAllEntries = (entries: any[]): void => {
      entries.forEach((e: OrderEntryDeepFragment) => {
        const cachedEntry: CachedEntry = {
          id: e.id,
          name: e.orderEntryCache?.value?.name ?? 'Unnamed Entry',
          resolvedQuantity: e.orderEntryCache?.value?.resolvedQuantity ?? null,
          color: e.orderEntryCache?.value?.color ?? null,
          resolvedElementIdentifiersDbid:
            e.orderEntryCache?.value?.resolvedElementIdentifiersDbid ?? null,
          subRows: e.orderEntryCache?.value?.subRows
            ? (e.orderEntryCache.value.subRows as CachedEntry[])
            : null,
          customFields: e.orderEntryCache?.value?.customFields ?? null,
        };

        allEntries.push(cachedEntry);

        // Recursively process subrows with the same ID generation logic as MobileOrderEntries
        if (cachedEntry.subRows) {
          const processSubRows = (
            subRows: any[],
            parentId: string,
            depth = 0
          ): void => {
            subRows.forEach((subRow, index) => {
              const uniqueId = `${parentId}_d${depth}_i${index}`;
              const processedSubRow = {
                ...subRow,
                id: uniqueId,
              };
              allEntries.push(processedSubRow);

              if (subRow.subRows && Array.isArray(subRow.subRows)) {
                processSubRows(subRow.subRows, uniqueId, depth + 1);
              }
            });
          };

          processSubRows(cachedEntry.subRows, e.id, 1);
        }
      });
    };

    collectAllEntries(orderData.order.orderEntries);

    // Filter to only selected entries
    return allEntries.filter((entry) => selectedIds.has(entry.id));
  }, [orderData?.order?.orderEntries, selectedIds, orderId]);

  const globalOffset =
    projectData?.project?.settings?.viewerGlobalOffset ?? null;

  // Helper function to get color for an element from a specific entry
  // eslint-disable-next-line complexity
  const getColorForElement = (
    entry: CachedEntry,
    modelUrn: string,
    dbId: number
  ): string | null => {
    const hasSubRows = entry.subRows && entry.subRows.length > 0;
    const isGradientColor = entry.color?.includes('gradient');

    if (isGradientColor && hasSubRows) {
      // Gradient entry: Check if this element belongs to any subrow first
      for (const subRow of entry.subRows ?? []) {
        if (
          subRow.resolvedElementIdentifiersDbid?.some(
            (e: any) => e.modelUrn === modelUrn && e.dbIds.includes(dbId)
          )
        ) {
          return extractFirstHexColor(subRow.color ?? '#000000');
        }
      }
      // If element belongs to gradient parent but not found in subrows, use parent color
      if (
        entry.resolvedElementIdentifiersDbid?.some(
          (e: any) => e.modelUrn === modelUrn && e.dbIds.includes(dbId)
        )
      ) {
        return extractFirstHexColor(entry.color ?? '#000000');
      }
    } else {
      // Non-gradient entry: Use entry's own color
      if (
        entry.resolvedElementIdentifiersDbid?.some(
          (e: any) => e.modelUrn === modelUrn && e.dbIds.includes(dbId)
        )
      ) {
        return extractFirstHexColor(entry.color ?? '#000000');
      }
    }
    return null; // Element doesn't belong to this entry
  };

  // ViewerContent applies effects based on the viewer from context.
  function ViewerContent({
    entries,
  }: {
    entries: CachedEntry[];
  }): React.ReactElement {
    const { viewer, loadedModels } = useViewer();
    const { isolate, show } = useVisibility();
    const { colorSection, clearColors } = useColorization();

    useEffect(() => {
      // Always clear previous colors and reset isolation when effect runs or entries change
      clearColors();
      show({}); // Reset isolation

      if (!entries.length) {
        isolate({});
        viewer?.resize();
        return;
      }

      const allElementsToHighlight: DbIdsPerModel = {};

      // Collect all elements from all selected entries
      entries.forEach((entry) => {
        entry.resolvedElementIdentifiersDbid?.forEach(({ modelUrn, dbIds }) => {
          allElementsToHighlight[modelUrn] = [
            ...(allElementsToHighlight[modelUrn] || []),
            ...dbIds,
          ];
        });
      });

      isolate(allElementsToHighlight);

      // For multi-selection, use a simple color strategy
      let colorFunc: (modelUrn: string, dbId: number) => string;

      if (entries.length === 1) {
        // Single entry selected - use helper function
        const entry = entries[0];
        colorFunc = (modelUrn: string, dbId: number): string => {
          return getColorForElement(entry, modelUrn, dbId) ?? '#000000';
        };
      } else {
        // Multiple entries selected - use helper function for each entry
        colorFunc = (modelUrn: string, dbId: number): string => {
          for (const entry of entries) {
            const color = getColorForElement(entry, modelUrn, dbId);
            if (color) {
              return color;
            }
          }
          return '#000000'; // Default black
        };
      }

      colorSection(colorFunc, allElementsToHighlight);

      viewer?.resize();

      return () => {
        clearColors();
        show({});
        viewer?.resize();
      };
    }, [
      entries,
      isolate,
      show,
      colorSection,
      clearColors,
      loadedModels,
      viewer,
    ]);
    // loadedModels is kept if colorFunc needs it, viewer if resize is direct.

    return <></>;
  }

  // Check if we're currently fetching and don't have required data yet
  // Only require orderId-related data when orderId is present
  // Note: globalOffset can be null for sheets-only projects and that's okay
  const isLoading =
    projectFetching || shapesFetching || (orderId && orderFetching);

  if (isLoading) {
    return (
      <Box p={4} textAlign="center" height="100vh">
        <Spinner />
      </Box>
    );
  }

  return (
    // This Box defines the overall screen boundaries for the mobile viewer page.
    <Box
      height="100vh"
      width="100vw"
      overflow="hidden"
      position="relative"
      sx={{
        '#forge-viewer, #sheets-viewer-v2': {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          height: '100%',
          width: '100%',
        },
        '.adsk-viewing-viewer, .canvas-wrap, canvas': {
          height: '100% !important',
          width: '100% !important',
        },
      }}
    >
      <AutodeskViewing
        projectId={projectId}
        setViewingInitialized={setIsViewingInitialized}
      >
        <ViewerModeProvider value={viewerModeContextValue}>
          <ForgeContextMenuProvider>
            <SheetContextMenuProvider>
              <MeshAlgorithmResultsProvider projectId={projectId}>
                <SheetViewerProvider
                  projectId={projectId}
                  shouldHideViewer={false}
                >
                  <ProjectHistoryProvider>
                    <ForgeViewerProvider
                      projectId={projectId}
                      isViewingInitialized={isViewingInitialized}
                      globalOffset={globalOffset}
                    >
                      <LegendEntriesProvider>
                        <SheetsViewerV2
                          projectId={projectId}
                          shouldHideViewer={false}
                        >
                          <>
                            <MobileViewerSettings />
                            <ViewerContent entries={selectedEntries} />
                            <ProjectShapesRenderer
                              projectId={projectId}
                              shapes={shapes}
                            />
                            <MobileSheetBootstrap />
                            <MobileSheetShapesLoader projectId={projectId} />
                          </>
                        </SheetsViewerV2>
                      </LegendEntriesProvider>
                    </ForgeViewerProvider>
                  </ProjectHistoryProvider>
                </SheetViewerProvider>
              </MeshAlgorithmResultsProvider>
            </SheetContextMenuProvider>
          </ForgeContextMenuProvider>
        </ViewerModeProvider>
      </AutodeskViewing>
    </Box>
  );
}
